import { useState, useEffect, useCallback, useMemo } from "react";
import {
  CheckCircle2,
  Clock,
  AlertTriangle,
  Calendar,
  Search,
  RotateCw,
  UserCheck,
  Palette,
  Factory,
  Send,
  Truck,
  FileText,
  ListTodo,
  ArrowRight,
  Building2,
  Phone,
  PhoneCall,
  User,
  Package,
  Filter,
  ShoppingCart,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { testid } from "../../lib/format";
import { api } from "../../lib/api";
import { isAdmin, formatRole, canViewClientPhone } from "../../lib/permissions";
import Pagination, { usePagination } from "./Pagination";
import { useSavedState } from "../../lib/listState";

function RecordFollowUpModal({ task, onClose, onSaved }) {
  const [interactionType, setInteractionType] = useState("call");
  const [summary, setSummary] = useState("");
  const [outcome, setOutcome] = useState("");
  const [completeCurrent, setCompleteCurrent] = useState(true);
  const [nextFollowUp, setNextFollowUp] = useState("");
  const [nextFollowUpNotes, setNextFollowUpNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!summary.trim()) {
      toast.error("Please enter a summary or notes of the interaction");
      return;
    }
    setSaving(true);
    try {
      await api("post", `/leads/${task.lead_id}/follow-up`, {
        interaction_type: interactionType,
        summary: summary.trim(),
        outcome: outcome.trim(),
        complete_current: completeCurrent,
        next_follow_up: nextFollowUp,
        follow_up_notes: nextFollowUpNotes.trim(),
      });
      toast.success("Follow-up interaction recorded successfully");
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record follow-up");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} style={{ zIndex: 1000 }}>
      <form
        className="modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        style={{ maxWidth: 500, width: "90%" }}
        data-testid="record-followup-modal"
      >
        <div className="modal-head">
          <div>
            <div className="small-label">SALES LEAD FOLLOW-UP</div>
            <h3 style={{ margin: "4px 0 2px" }}>Record Interaction</h3>
            <small style={{ color: "#64748b" }}>
              {task.client_name || task.name} {task.lead_id ? `(Lead #${task.lead_id})` : ""}
            </small>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="modal-close-btn"
            style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b" }}
          >
            <X size={18} />
          </button>
        </div>

        <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 14, padding: "16px 0" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13, fontWeight: 600 }}>
            Interaction Type
            <select
              value={interactionType}
              onChange={(e) => setInteractionType(e.target.value)}
              data-testid="followup-type-select"
              className="dept-select"
              style={{ width: "100%", padding: "8px 10px" }}
            >
              <option value="call">Phone Call</option>
              <option value="meeting">Meeting / Discussion</option>
              <option value="email">Email</option>
              <option value="note">Internal Note</option>
            </select>
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13, fontWeight: 600 }}>
            Interaction Summary / Notes *
            <textarea
              required
              rows={3}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Discussed quotation, client requested product sample, etc."
              data-testid="followup-summary-input"
              style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 13 }}
            />
          </label>

          <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13, fontWeight: 600 }}>
            Outcome
            <input
              type="text"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              placeholder="e.g. Interested, Proposal Sent, Follow up next week, Sample requested"
              data-testid="followup-outcome-input"
              style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 13 }}
            />
          </label>

          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
            <input
              type="checkbox"
              id="complete-current-checkbox"
              checked={completeCurrent}
              onChange={(e) => setCompleteCurrent(e.target.checked)}
              data-testid="followup-complete-checkbox"
              style={{ cursor: "pointer", width: 16, height: 16 }}
            />
            <label htmlFor="complete-current-checkbox" style={{ fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Mark current scheduled follow-up as completed
            </label>
          </div>

          <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13, fontWeight: 600 }}>
            Schedule Next Follow-Up Date (optional)
            <input
              type="date"
              value={nextFollowUp}
              onChange={(e) => setNextFollowUp(e.target.value)}
              data-testid="followup-next-date-input"
              style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 13 }}
            />
          </label>

          {nextFollowUp && (
            <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13, fontWeight: 600 }}>
              Next Follow-Up Reminder / Agenda (optional)
              <input
                type="text"
                placeholder="Brief reminder / agenda..."
                value={nextFollowUpNotes}
                onChange={(e) => setNextFollowUpNotes(e.target.value)}
                data-testid="followup-next-notes-input"
                style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 13 }}
              />
            </label>
          )}
        </div>

        <div className="modal-actions" style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 12, borderTop: "1px solid #e2e8f0" }}>
          <button type="button" className="btn-secondary" onClick={onClose} data-testid="followup-cancel-btn">
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving} data-testid="followup-save-btn">
            {saving ? "Saving…" : "Save Interaction"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ActivityMetric({ label, value, caption, tone = "blue", active = false, onClick, testId }) {
  return (
    <div
      className={`metric ${tone} clickable ${active ? "active" : ""}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick && onClick();
        }
      }}
      aria-pressed={active}
      style={{
        cursor: "pointer",
        position: "relative",
        userSelect: "none",
      }}
      data-testid={testId || `metric-${testid(label)}`}
    >
      <div className="metric-top">
        <span>{label}</span>
        <span
          className="metric-nav-arrow"
          style={{
            display: "inline-flex",
            alignItems: "center",
            opacity: active ? 1 : 0.45,
            color: active ? "var(--blue)" : "#94a3b8",
            fontWeight: 700,
            fontSize: 13,
            transition: "all 0.15s ease",
          }}
          aria-hidden="true"
        >
          {active ? "●" : "→"}
        </span>
      </div>
      <strong>{value}</strong>
      <small>{caption}</small>
    </div>
  );
}

export default function Activities({
  user,
  onOpen,
  onNavigate,
  openDesign,
  openProduction,
  openDispatch,
  openDelivery,
  openClient,
  openLead,
  openProcurement,
  routeParams,
}) {
  const [activities, setActivities] = useState([]);
  const [counts, setCounts] = useState({ all: 0, today: 0, in_progress: 0, overdue: 0, completed: 0 });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useSavedState("activities", "activeTab", routeParams?.tab || routeParams?.activeTab || "today"); // Default: Today's Tasks
  const [deptFilter, setDeptFilter] = useSavedState("activities", "deptFilter", "");
  const [priorityFilter, setPriorityFilter] = useSavedState("activities", "priorityFilter", "");
  const [searchQuery, setSearchQuery] = useSavedState("activities", "searchQuery", "");
  const [followupFilter, setFollowupFilter] = useSavedState("activities", "followupFilter", "all");
  const [assignedUserFilter, setAssignedUserFilter] = useSavedState("activities", "assignedUserFilter", "");
  const [followUpModalTask, setFollowUpModalTask] = useState(null);

  useEffect(() => {
    if (routeParams?.tab) {
      setActiveTab(routeParams.tab === "follow-ups" ? "followups" : routeParams.tab);
    } else if (routeParams?.activeTab) {
      setActiveTab(routeParams.activeTab === "follow-ups" ? "followups" : routeParams.activeTab);
    }
    if (routeParams?.followupFilter) {
      setFollowupFilter(routeParams.followupFilter);
    }
  }, [routeParams?.tab, routeParams?.activeTab, routeParams?.followupFilter, setActiveTab, setFollowupFilter]);

  const isAdminOrManagement = isAdmin(user);

  const loadActivities = useCallback(async () => {
    setLoading(true);
    try {
      const url = deptFilter
        ? `/activities?filter_role=${encodeURIComponent(deptFilter)}`
        : `/activities`;
      const res = await api("get", url);
      if (res?.data) {
        setActivities(res.data.tasks || []);
        setCounts(res.data.counts || { all: 0, today: 0, in_progress: 0, overdue: 0, completed: 0 });
      }
    } catch (err) {
      console.error("Failed to fetch activities:", err);
    } finally {
      setLoading(false);
    }
  }, [deptFilter]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  const availableSalesUsers = useMemo(() => {
    const set = new Set();
    activities.forEach((t) => {
      if (t.is_followup && t.assigned_to && t.assigned_to !== "Unassigned") {
        set.add(t.assigned_to);
      }
    });
    return Array.from(set).sort();
  }, [activities]);

  const followupCounts = useMemo(() => {
    const baseFollowups = activities.filter((t) => {
      if (!t.is_followup) return false;
      if (assignedUserFilter && (t.assigned_to || "").toLowerCase() !== assignedUserFilter.toLowerCase()) return false;
      if (priorityFilter && (t.priority || "Normal").toLowerCase() !== priorityFilter.toLowerCase()) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchName = t.name?.toLowerCase().includes(q);
        const matchClient = t.client_name?.toLowerCase().includes(q);
        const matchLeadId = t.lead_id?.toString().toLowerCase().includes(q);
        const matchAssigned = t.assigned_to?.toLowerCase().includes(q);
        return matchName || matchClient || matchLeadId || matchAssigned;
      }
      return true;
    });

    return {
      all: baseFollowups.length,
      today: baseFollowups.filter((t) => t.followup_status === "today" || t.state === "today").length,
      upcoming: baseFollowups.filter((t) => t.followup_status === "upcoming").length,
      overdue: baseFollowups.filter((t) => t.followup_status === "overdue" || t.state === "overdue").length,
      completed: baseFollowups.filter((t) => t.followup_status === "completed" || t.state === "completed").length,
    };
  }, [activities, assignedUserFilter, priorityFilter, searchQuery]);

  const filteredTasks = useMemo(() => {
    return activities.filter((task) => {
      // 1. Tab filter
      if (activeTab === "followups") {
        if (!task.is_followup) return false;
        if (followupFilter === "today") {
          if (task.followup_status !== "today" && task.state !== "today") return false;
        } else if (followupFilter === "upcoming") {
          if (task.followup_status !== "upcoming") return false;
        } else if (followupFilter === "overdue") {
          if (task.followup_status !== "overdue" && task.state !== "overdue") return false;
        } else if (followupFilter === "completed") {
          if (task.followup_status !== "completed" && task.state !== "completed") return false;
        }
        if (assignedUserFilter && (task.assigned_to || "").toLowerCase() !== assignedUserFilter.toLowerCase()) {
          return false;
        }
      } else if (activeTab !== "all" && task.state !== activeTab) {
        return false;
      }

      // 2. Priority filter
      if (priorityFilter && (task.priority || "Normal").toLowerCase() !== priorityFilter.toLowerCase()) {
        return false;
      }

      // 3. Search query filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchName = task.name?.toLowerCase().includes(q);
        const matchClient = task.client_name?.toLowerCase().includes(q);
        const matchOrderId = task.order_id?.toString().toLowerCase().includes(q);
        const matchLeadId = task.lead_id?.toString().toLowerCase().includes(q);
        const matchStatus = task.status?.toLowerCase().includes(q);
        const matchAssigned = task.assigned_to?.toLowerCase().includes(q);
        const matchDept = task.department?.toLowerCase().includes(q);
        const matchProduct = task.metadata?.product?.toLowerCase().includes(q);
        const matchContact = task.metadata?.contact?.toLowerCase().includes(q);
        const matchChallan = task.metadata?.challan?.toLowerCase().includes(q);
        const matchNotes = task.metadata?.notes?.toLowerCase().includes(q);

        return (
          matchName ||
          matchClient ||
          matchOrderId ||
          matchLeadId ||
          matchStatus ||
          matchAssigned ||
          matchDept ||
          matchProduct ||
          matchContact ||
          matchChallan ||
          matchNotes
        );
      }
      return true;
    });
  }, [activities, activeTab, followupFilter, assignedUserFilter, priorityFilter, searchQuery]);

  // Dynamic counts reflecting priority filter & search query so counts match displayed records
  const taskCounts = useMemo(() => {
    const hasSubFilters = Boolean(priorityFilter || searchQuery.trim() || assignedUserFilter);
    if (!hasSubFilters) {
      return {
        ...counts,
        followups: counts.followups ?? activities.filter((t) => t.is_followup).length,
        followups_all: counts.followups_all ?? activities.filter((t) => t.is_followup).length,
        followups_today: counts.followups_today ?? activities.filter((t) => t.is_followup && (t.followup_status === "today" || t.state === "today")).length,
        followups_upcoming: counts.followups_upcoming ?? activities.filter((t) => t.is_followup && t.followup_status === "upcoming").length,
        followups_overdue: counts.followups_overdue ?? activities.filter((t) => t.is_followup && (t.followup_status === "overdue" || t.state === "overdue")).length,
        followups_completed: counts.followups_completed ?? activities.filter((t) => t.is_followup && (t.followup_status === "completed" || t.state === "completed")).length,
      };
    }

    const matchesPriorityAndSearch = (task) => {
      if (priorityFilter && (task.priority || "Normal").toLowerCase() !== priorityFilter.toLowerCase()) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchName = task.name?.toLowerCase().includes(q);
        const matchClient = task.client_name?.toLowerCase().includes(q);
        const matchOrderId = task.order_id?.toString().toLowerCase().includes(q);
        const matchLeadId = task.lead_id?.toString().toLowerCase().includes(q);
        const matchStatus = task.status?.toLowerCase().includes(q);
        const matchAssigned = task.assigned_to?.toLowerCase().includes(q);
        const matchDept = task.department?.toLowerCase().includes(q);
        const matchProduct = task.metadata?.product?.toLowerCase().includes(q);
        const matchContact = task.metadata?.contact?.toLowerCase().includes(q);
        const matchChallan = task.metadata?.challan?.toLowerCase().includes(q);
        const matchNotes = task.metadata?.notes?.toLowerCase().includes(q);

        return (
          matchName ||
          matchClient ||
          matchOrderId ||
          matchLeadId ||
          matchStatus ||
          matchAssigned ||
          matchDept ||
          matchProduct ||
          matchContact ||
          matchChallan ||
          matchNotes
        );
      }
      return true;
    };

    const subFiltered = activities.filter(matchesPriorityAndSearch);
    return {
      all: subFiltered.length,
      today: subFiltered.filter((t) => t.state === "today").length,
      in_progress: subFiltered.filter((t) => t.state === "in_progress").length,
      overdue: subFiltered.filter((t) => t.state === "overdue").length,
      completed: subFiltered.filter((t) => t.state === "completed").length,
      followups: subFiltered.filter((t) => t.is_followup).length,
      followups_all: subFiltered.filter((t) => t.is_followup).length,
      followups_today: subFiltered.filter((t) => t.is_followup && (t.followup_status === "today" || t.state === "today")).length,
      followups_upcoming: subFiltered.filter((t) => t.is_followup && t.followup_status === "upcoming").length,
      followups_overdue: subFiltered.filter((t) => t.is_followup && (t.followup_status === "overdue" || t.state === "overdue")).length,
      followups_completed: subFiltered.filter((t) => t.is_followup && (t.followup_status === "completed" || t.state === "completed")).length,
    };
  }, [activities, counts, priorityFilter, searchQuery, assignedUserFilter]);

  const { currentPage, setCurrentPage, pageSize, setPageSize, paginatedItems, totalItems } = usePagination(filteredTasks, 10, { sectionKey: "activities" });

  const handleTaskAction = (task, e) => {
    if (e) e.stopPropagation();
    const target = task.action_target;
    if (target === "Leads") {
      if (task.lead_id && openLead) openLead(task.lead_id);
      else onNavigate("Leads");
    } else if (target === "Clients") {
      if (task.client_id && openClient) openClient(task.client_id);
      else onNavigate("Clients");
    } else if (target === "DesignDetail") {
      if (task.order_id && openDesign) openDesign(task.order_id);
      else onNavigate("Design");
    } else if (target === "ProductionDetail") {
      if (task.order_id && openProduction) openProduction(task.order_id);
      else onNavigate("Production");
    } else if (target === "DispatchDetail") {
      if (task.order_id && openDispatch) openDispatch(task.order_id);
      else onNavigate("Dispatch");
    } else if (target === "DeliveryDetail") {
      if (task.order_id && openDelivery) openDelivery(task.order_id);
      else onNavigate("Delivery");
    } else if (target === "OrderDetail") {
      if (task.order_id && onOpen) onOpen(task.order_id);
      else onNavigate("Orders");
    } else if (target === "Procurement") {
      onNavigate("Procurement");
    } else {
      onNavigate("Orders");
    }
  };

  const getActionIcon = (task) => {
    switch (task.department) {
      case "Sales":
        return <UserCheck size={14} />;
      case "Design":
        return <Palette size={14} />;
      case "Production":
        return <Factory size={14} />;
      case "Dispatch":
        return <Send size={14} />;
      case "Delivery":
        return <Truck size={14} />;
      case "Accounts":
        return <FileText size={14} />;
      case "Procurement":
        return <ShoppingCart size={14} />;
      default:
        return <ArrowRight size={14} />;
    }
  };

  return (
    <div className="content" data-testid="activities-page">
      {/* Page Heading */}
      <div className="page-heading">
        <div>
          <div className="small-label">
            {new Date()
              .toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
              .toUpperCase()}
          </div>
          <h1>Daily Activities & Tasks</h1>
          <p className="muted">
            Personalized workflow queue and operational action items for{" "}
            <b style={{ color: "var(--navy)" }}>{user?.name || "User"}</b> (
            <span style={{ fontWeight: 600 }}>{formatRole(user?.role)}{user?.department ? ` · ${user.department}` : ""}</span>).
          </p>
        </div>

        <div className="activity-controls">
          {isAdminOrManagement && (
            <div className="dept-select-wrap">
              <label htmlFor="dept-filter">Department:</label>
              <select
                id="dept-filter"
                className="dept-select"
                value={deptFilter}
                onChange={(e) => {
                  setDeptFilter(e.target.value);
                  setCurrentPage(1);
                }}
                data-testid="activities-dept-filter"
              >
                <option value="">All Departments</option>
                <option value="sales">Sales (Leads & Clients)</option>
                <option value="design">Design Studio</option>
                <option value="production">Production (Shop Floor & Dispatch)</option>
                <option value="delivery">Delivery</option>
                <option value="accounts">Accounts & Invoicing</option>
              </select>
            </div>
          )}

          <button
            className={`refresh-activity-btn ${loading ? "spinning" : ""}`}
            onClick={loadActivities}
            title="Refresh Tasks"
            data-testid="activities-refresh-btn"
          >
            <RotateCw size={15} />
          </button>
        </div>
      </div>

      {/* Summary Metrics Strip */}
      <div className="metrics-grid">
        <ActivityMetric
          label="Today's Tasks"
          value={taskCounts.today}
          caption="Scheduled for today"
          tone="blue"
          active={activeTab === "today"}
          onClick={() => {
            setActiveTab("today");
            setCurrentPage(1);
          }}
          testId="metric-today-tasks"
        />
        <ActivityMetric
          label="In Progress"
          value={taskCounts.in_progress}
          caption="Actively in workflow"
          tone="green"
          active={activeTab === "in_progress"}
          onClick={() => {
            setActiveTab("in_progress");
            setCurrentPage(1);
          }}
          testId="metric-in-progress"
        />
        <ActivityMetric
          label="Overdue Tasks"
          value={taskCounts.overdue}
          caption="Requires immediate action"
          tone={taskCounts.overdue > 0 ? "red" : "blue"}
          active={activeTab === "overdue"}
          onClick={() => {
            setActiveTab("overdue");
            setCurrentPage(1);
          }}
          testId="metric-overdue"
        />
        <ActivityMetric
          label="Completed Today"
          value={taskCounts.completed}
          caption="Resolved / dispatched"
          tone="green"
          active={activeTab === "completed"}
          onClick={() => {
            setActiveTab("completed");
            setCurrentPage(1);
          }}
          testId="metric-completed"
        />
      </div>

      {/* Main Activities Panel */}
      <section className="activity-panel" data-testid="activities-main-panel">
        {/* Subbar: Filter Tabs, Priority Selector & Search */}
        <div className="activity-subbar">
          <div className="activity-tabs">
            <button
              className={`activity-tab-btn ${activeTab === "today" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("today");
                setCurrentPage(1);
              }}
              data-testid="tab-today"
            >
              <Clock size={14} />
              Today’s Tasks
              <span className="activity-tab-pill">{taskCounts.today}</span>
            </button>
            <button
              className={`activity-tab-btn ${activeTab === "in_progress" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("in_progress");
                setCurrentPage(1);
              }}
              data-testid="tab-in_progress"
            >
              In Progress
              <span className="activity-tab-pill">{taskCounts.in_progress}</span>
            </button>
            <button
              className={`activity-tab-btn ${activeTab === "overdue" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("overdue");
                setCurrentPage(1);
              }}
              data-testid="tab-overdue"
            >
              <AlertTriangle size={14} />
              Overdue
              <span className={`activity-tab-pill ${taskCounts.overdue > 0 ? "urgent-badge" : ""}`}>
                {taskCounts.overdue}
              </span>
            </button>
            <button
              className={`activity-tab-btn ${activeTab === "completed" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("completed");
                setCurrentPage(1);
              }}
              data-testid="tab-completed"
            >
              <CheckCircle2 size={14} />
              Completed
              <span className="activity-tab-pill">{taskCounts.completed}</span>
            </button>
            <button
              className={`activity-tab-btn ${activeTab === "all" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("all");
                setCurrentPage(1);
              }}
              data-testid="tab-all"
            >
              All Activities
              <span className="activity-tab-pill">{taskCounts.all}</span>
            </button>
            <button
              className={`activity-tab-btn ${activeTab === "followups" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("followups");
                setCurrentPage(1);
              }}
              data-testid="tab-followups"
            >
              <PhoneCall size={14} />
              Follow-ups
              <span className="activity-tab-pill">{taskCounts.followups || 0}</span>
            </button>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div className="dept-select-wrap">
              <select
                className="dept-select"
                value={priorityFilter}
                onChange={(e) => {
                  setPriorityFilter(e.target.value);
                  setCurrentPage(1);
                }}
                data-testid="priority-filter-select"
                style={{ fontSize: 12, padding: "6px 10px" }}
              >
                <option value="">All Priorities</option>
                <option value="urgent">Urgent Priority</option>
                <option value="high">High Priority</option>
                <option value="normal">Normal Priority</option>
                <option value="low">Low Priority</option>
              </select>
            </div>

            <div className="activity-search-box">
              <Search size={14} />
              <input
                type="text"
                placeholder="Search tasks, clients, IDs…"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                data-testid="activity-search-input"
              />
            </div>
          </div>
        </div>

        {/* Dedicated Follow-ups Subfilters Strip */}
        {activeTab === "followups" && (
          <div
            className="followup-subfilters-bar"
            data-testid="followup-subfilters-bar"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "10px 16px",
              background: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#64748b",
                  marginRight: 4,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Filter size={13} /> Sub-filters:
              </span>
              <button
                type="button"
                className={`activity-subtab-btn ${followupFilter === "all" ? "active" : ""}`}
                onClick={() => {
                  setFollowupFilter("all");
                  setCurrentPage(1);
                }}
                data-testid="subfilter-followup-all"
                style={{
                  padding: "5px 12px",
                  borderRadius: 20,
                  border: followupFilter === "all" ? "1px solid var(--blue, #2563eb)" : "1px solid #cbd5e1",
                  background: followupFilter === "all" ? "var(--blue, #2563eb)" : "#ffffff",
                  color: followupFilter === "all" ? "#ffffff" : "#475569",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                All Follow-ups <span style={{ opacity: 0.85, fontSize: 11 }}>({followupCounts.all})</span>
              </button>
              <button
                type="button"
                className={`activity-subtab-btn ${followupFilter === "today" ? "active" : ""}`}
                onClick={() => {
                  setFollowupFilter("today");
                  setCurrentPage(1);
                }}
                data-testid="subfilter-followup-today"
                style={{
                  padding: "5px 12px",
                  borderRadius: 20,
                  border: followupFilter === "today" ? "1px solid #0284c7" : "1px solid #cbd5e1",
                  background: followupFilter === "today" ? "#0284c7" : "#ffffff",
                  color: followupFilter === "today" ? "#ffffff" : "#475569",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Today’s Follow-ups <span style={{ opacity: 0.85, fontSize: 11 }}>({followupCounts.today})</span>
              </button>
              <button
                type="button"
                className={`activity-subtab-btn ${followupFilter === "upcoming" ? "active" : ""}`}
                onClick={() => {
                  setFollowupFilter("upcoming");
                  setCurrentPage(1);
                }}
                data-testid="subfilter-followup-upcoming"
                style={{
                  padding: "5px 12px",
                  borderRadius: 20,
                  border: followupFilter === "upcoming" ? "1px solid #7c3aed" : "1px solid #cbd5e1",
                  background: followupFilter === "upcoming" ? "#7c3aed" : "#ffffff",
                  color: followupFilter === "upcoming" ? "#ffffff" : "#475569",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Upcoming <span style={{ opacity: 0.85, fontSize: 11 }}>({followupCounts.upcoming})</span>
              </button>
              <button
                type="button"
                className={`activity-subtab-btn ${followupFilter === "overdue" ? "active" : ""}`}
                onClick={() => {
                  setFollowupFilter("overdue");
                  setCurrentPage(1);
                }}
                data-testid="subfilter-followup-overdue"
                style={{
                  padding: "5px 12px",
                  borderRadius: 20,
                  border: followupFilter === "overdue" ? "1px solid #dc2626" : "1px solid #cbd5e1",
                  background: followupFilter === "overdue" ? "#dc2626" : "#ffffff",
                  color: followupFilter === "overdue" ? "#ffffff" : "#475569",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Overdue <span style={{ opacity: 0.85, fontSize: 11 }}>({followupCounts.overdue})</span>
              </button>
              <button
                type="button"
                className={`activity-subtab-btn ${followupFilter === "completed" ? "active" : ""}`}
                onClick={() => {
                  setFollowupFilter("completed");
                  setCurrentPage(1);
                }}
                data-testid="subfilter-followup-completed"
                style={{
                  padding: "5px 12px",
                  borderRadius: 20,
                  border: followupFilter === "completed" ? "1px solid #16a34a" : "1px solid #cbd5e1",
                  background: followupFilter === "completed" ? "#16a34a" : "#ffffff",
                  color: followupFilter === "completed" ? "#ffffff" : "#475569",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                Completed <span style={{ opacity: 0.85, fontSize: 11 }}>({followupCounts.completed})</span>
              </button>
            </div>

            {availableSalesUsers.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <label
                  htmlFor="followup-assigned-filter"
                  style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}
                >
                  Assigned User:
                </label>
                <select
                  id="followup-assigned-filter"
                  className="dept-select"
                  value={assignedUserFilter}
                  onChange={(e) => {
                    setAssignedUserFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  data-testid="followup-assigned-user-filter"
                  style={{ fontSize: 12, padding: "5px 10px" }}
                >
                  <option value="">All Users</option>
                  {availableSalesUsers.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Task Cards List */}
        <div className="activity-tasks-container">
          {filteredTasks.length === 0 ? (
            <div className="activity-empty-state" data-testid="activity-empty-state">
              <div className="activity-empty-icon">
                <CheckCircle2 size={28} />
              </div>
              <h3>
                {searchQuery
                  ? "No matching activities found"
                  : priorityFilter
                  ? `No ${priorityFilter} priority activities found`
                  : activeTab === "today"
                  ? "All caught up for today!"
                  : activeTab === "overdue"
                  ? "No overdue activities!"
                  : activeTab === "completed"
                  ? "No completed activities yet"
                  : activeTab === "followups"
                  ? "No follow-ups found"
                  : "No activities found in this view"}
              </h3>
              <p>
                {searchQuery
                  ? `No activities matched your query "${searchQuery}". Try clearing search or filters.`
                  : activeTab === "today"
                  ? "You have completed all scheduled tasks for today, or none are currently pending."
                  : activeTab === "overdue"
                  ? "Great work! All assignments, proofs, and follow-ups are running on schedule."
                  : activeTab === "followups"
                  ? "There are no scheduled follow-ups matching the current filters."
                  : "Switch tabs or select another department to view other assignments."}
              </p>
            </div>
          ) : (
            paginatedItems.map((task) => {
              const priorityClass = `priority-${(task.priority || "normal").toLowerCase()}`;
              const deptClass = (task.department || "sales").toLowerCase();
              const isDone = task.state === "completed";

              return (
                <div
                  key={task.id}
                  className={`activity-task-card ${priorityClass} ${isDone ? "completed-card" : ""}`}
                  data-testid={`task-card-${task.id}`}
                  onClick={(e) => handleTaskAction(task, e)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="activity-task-content">
                    {/* Badges Row */}
                    <div className="activity-task-badges">
                      <span className={`dept-tag ${deptClass}`}>
                        {task.department}
                      </span>
                      <span className={`priority-tag ${(task.priority || "normal").toLowerCase()}`}>
                        {task.priority} Priority
                      </span>
                      <span className={`state-tag ${task.state}`}>
                        {task.state === "overdue" && <AlertTriangle size={11} />}
                        {task.state === "today" && <Clock size={11} />}
                        {task.state === "in_progress" && <Clock size={11} />}
                        {task.state === "completed" && <CheckCircle2 size={11} />}
                        {task.is_followup
                          ? task.followup_status === "overdue"
                            ? "Overdue Follow-up"
                            : task.followup_status === "today"
                            ? "Due Today"
                            : task.followup_status === "completed"
                            ? "Follow-up Completed"
                            : "Upcoming Follow-up"
                          : task.state === "overdue"
                          ? "Overdue"
                          : task.state === "today"
                          ? "Due Today"
                          : task.state === "in_progress"
                          ? "In Progress"
                          : "Completed"}
                      </span>
                    </div>

                    {/* Task Title & Entities */}
                    <h4 className="activity-task-title">
                      <span>{task.name}</span>
                      {task.order_id && (
                        <span className="activity-task-entity-chip">#{task.order_id}</span>
                      )}
                      {task.lead_id && (
                        <span className="activity-task-entity-chip">Lead #{task.lead_id}</span>
                      )}
                    </h4>

                    {/* Meta Information Row */}
                    <div className="activity-task-meta-row">
                      {task.client_name && (
                        <span className="activity-meta-item">
                          <Building2 size={13} />
                          <b>{task.client_name}</b>
                        </span>
                      )}
                      {task.metadata?.product && (
                        <span className="activity-meta-item">
                          <Package size={13} />
                          <span>{task.metadata.product}</span>
                          {task.metadata.qty && <small>({task.metadata.qty} units)</small>}
                        </span>
                      )}
                      {task.metadata?.contact && (
                        <span className="activity-meta-item">
                          <User size={13} />
                          <span>{task.metadata.contact}</span>
                          {canViewClientPhone(user) && task.metadata.phone && (
                            <small style={{ marginLeft: 4 }}>
                              <Phone
                                size={11}
                                style={{ display: "inline", verticalAlign: "middle", marginRight: 2 }}
                              />
                              {task.metadata.phone}
                            </small>
                          )}
                        </span>
                      )}
                      {task.metadata?.challan && (
                        <span className="activity-meta-item">
                          <FileText size={13} />
                          <span>Challan: <b>{task.metadata.challan}</b></span>
                        </span>
                      )}
                      {task.due_date && (
                        <span
                          className="activity-meta-item"
                          style={{ color: task.state === "overdue" ? "#dc2626" : undefined }}
                        >
                          <Calendar size={13} />
                          <span>
                            {task.state === "overdue" ? "Overdue: " : "Due: "}
                            <b>{task.due_date}</b>
                          </span>
                        </span>
                      )}
                      {task.assigned_to && (
                        <span className="activity-meta-item">
                          <UserCheck size={13} />
                          <span>Assignee: <b>{task.assigned_to}</b></span>
                        </span>
                      )}
                      {(task.metadata?.follow_up_notes || task.metadata?.agenda || (task.is_followup && task.metadata?.notes)) && (
                        <span
                          className="activity-meta-item"
                          style={{
                            maxWidth: 360,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: "#1e293b",
                          }}
                          title={task.metadata.follow_up_notes || task.metadata.agenda || task.metadata.notes}
                          data-testid={`task-followup-agenda-${task.id}`}
                        >
                          <FileText size={13} style={{ color: "#2563eb", flexShrink: 0 }} />
                          <span style={{ fontWeight: 600, color: "#64748b" }}>Agenda:</span>
                          <span>{task.metadata.follow_up_notes || task.metadata.agenda || task.metadata.notes}</span>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Contextual Action Button */}
                  <div className="activity-task-actions" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {task.is_followup && task.lead_id && task.state !== "completed" && (
                      <button
                        className="activity-action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFollowUpModalTask(task);
                        }}
                        data-testid={`task-followup-btn-${task.id}`}
                        style={{ background: "#2563eb", color: "#ffffff", border: "none" }}
                        title="Record follow-up interaction"
                      >
                        <UserCheck size={14} />
                        Follow Up
                      </button>
                    )}
                    <button
                      className={`activity-action-btn ${isDone ? "secondary" : ""}`}
                      onClick={(e) => handleTaskAction(task, e)}
                      data-testid={`task-action-btn-${task.id}`}
                    >
                      {getActionIcon(task)}
                      {task.is_followup && task.lead_id ? "Open Lead" : task.action_label}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          itemLabel="activities"
        />
      </section>

      {followUpModalTask && (
        <RecordFollowUpModal
          task={followUpModalTask}
          onClose={() => setFollowUpModalTask(null)}
          onSaved={loadActivities}
        />
      )}
    </div>
  );
}
